import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../../components/MyAppText';
import moment from 'moment';
import { translate } from '../../../../services/translate';
import Badge from '../../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../../styles/colorPalettes';
import { ScrollView } from 'react-native-web-hover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faUserGroup } from '@fortawesome/free-solid-svg-icons';
import { formatRelativeTime } from '../../../deac/DeacCommons';

interface Props {
    vacancy: VacancyWithErrors;
    onPress: () => void;
}

export default function VacancyCard({ vacancy, onPress }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const remainingVacancies = vacancy.vacancy.quantity - (vacancy.vacancy.applicationsCount ?? 0);
    const [presentErrors, setPresentErrors] = useState<boolean>(false);

    function getVacancyTagTextColor(color: string) {
        const index = DEFAULT_PASTEL_COLORS.indexOf(color);
        return DEFAULT_PASTEL_TEXT_COLORS[index];
    }

    function displayRemainingVacanciesBadge(): boolean {
        let percentage = 0.01;
        if (vacancy.vacancy.quantity <= 100) {
            percentage = 0.25;
        } else if (vacancy.vacancy.quantity > 100 && vacancy.vacancy.quantity <= 500) {
            percentage = 0.1;
        } else if (vacancy.vacancy.quantity > 500 && vacancy.vacancy.quantity <= 1000) {
            percentage = 0.05;
        }

        const threshold = Math.ceil(vacancy.vacancy.quantity * percentage);
        return remainingVacancies < threshold;
    }

    function getErrorMessageTranslated(message: string) {
        if (message === 'agentWithoutSchedule') {
            return translate('agentWithoutSchedule');
        } else if (message === 'invalidVacancyLevelId') {
            return translate('invalidVacancyLevelId');
        } else if (message === 'invalidSkills') {
            return translate('invalidSkills');
        } else if (message === 'blockedWorkplace') {
            return translate('blockedWorkplace');
        } else if (message === 'conflictWithWorkInterval') {
            return translate('conflictWithWorkInterval');
        }

        return translate('unexpectedError');
    }

    return (
        <TouchableOpacity
            style={styles.card(vacancy.errors != undefined && vacancy.errors.length > 0)}
            onPress={() => {
                if (!vacancy.errors || vacancy.errors.length === 0) {
                    onPress();
                } else {
                    setPresentErrors(!presentErrors);
                }
            }}
        >
            {vacancy.errors && vacancy.errors.length > 0 && <FontAwesomeIcon style={{ position: 'absolute', top: -8, left: -8, zIndex: 9999, overflow: 'unset' }} icon={faTriangleExclamation} fontSize={17} color={theme.colors.errors['300']} />}

            <View style={styles.cardHeader}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <MyAppText style={styles.cardTitle}>{vacancy.vacancy.title}</MyAppText>
                    <MyAppText style={styles.cardTime}>{formatRelativeTime(vacancy.vacancy.applicationsStart)}</MyAppText>
                </View>
                <View style={{ flexDirection: 'column', rowGap: 4 }}>
                    <MyAppText style={styles.cardWorkplace}>{vacancy.vacancy.workplace.name}</MyAppText>
                    <MyAppText style={styles.cardWorkplace}>{moment(vacancy.vacancy.activityStart).format('DD/MM HH:mm')} - {moment(vacancy.vacancy.activityFinish).format('DD/MM HH:mm')}</MyAppText>
                </View>
            </View>
            <ScrollView contentContainerStyle={styles.tagsContainer}>
                {vacancy.vacancy.tags?.map(tag =>
                    <Badge
                        mode='ROUNDED'
                        key={tag.id}
                        backgroundColor={tag.color}
                        textColor={getVacancyTagTextColor(tag.color)}
                    >
                        {tag.name}
                    </Badge>
                )}
            </ScrollView>
            <View style={styles.cardFooter}>
                <View style={{ flexDirection: 'row', columnGap: 2 }}>
                    <FontAwesomeIcon icon={faUserGroup} fontSize={17} color={theme.colors.neutral['400']} />
                    <MyAppText style={styles.cardVacanciesText}>{vacancy.vacancy.quantity} {translate('vacancies').toLowerCase()}</MyAppText>
                </View>
                {displayRemainingVacanciesBadge()
                    ? <Badge
                        mode='ROUNDED'
                        backgroundColor={theme.colors.errors['200']}
                        textColor={theme.colors.white['500']}
                        size='small'
                    >
                        {remainingVacancies} {translate('remainingVacancies').toLowerCase()}
                    </Badge>
                    : null}
            </View>
            {presentErrors && <View>
                {vacancy.errors?.map((errorMessage, index) => (
                    <MyAppText key={index} style={styles.errorMessageStyle}>{getErrorMessageTranslated(errorMessage.message)}</MyAppText>
                ))}
            </View>
            }
        </TouchableOpacity>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    card: (hasErrors: boolean) => ({
        padding: 12,
        borderWidth: 1,
        borderColor: hasErrors ? theme.colors.errors['300'] : theme.colors.neutral['100'],
        borderRadius: 8,
        gap: 16,
        backgroundColor: theme.colors.white['500']
    }),
    cardHeader: {
        flexDirection: 'column',
        rowGap: 8,
    },
    cardTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    cardTime: {
        fontSize: 10,
        fontWeight: '600',
        color: theme.colors.neutral['400']
    },
    cardWorkplace: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.neutral['400']
    },
    tagsContainer: {
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        maxHeight: 80,
    },
    cardFooter: {
        flexDirection: 'row',
        columnGap: 8,
    },
    cardVacanciesText: {
        fontSize: 12,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    errorMessageStyle: {
        fontSize: 10,
        fontWeight: '600',
        color: theme.colors.errors['300']
    }
}));
